import { useAtomValue } from 'jotai'
import { hazardByIdLoadableAtom } from '../state/data-submodule.atoms'
import { scenarioFiltersAtomFamily } from '@app/domains/hazards/state/hazard.atoms'
import { resolveScenarioGridParameters } from '@app/domains/hazards/utils/hazardScenarioGridParameters.utils'
import {
  filterHazardScenarios,
  resolveFilterableParameters,
} from '@app/domains/hazards/utils/hazardScenarioFilters.utils'
import { buildScenarioGrid } from '@app/domains/hazards/utils/hazardScenarioGrid.utils'
import { useHazardScenarioFilters } from '@app/domains/hazards/hooks/useHazardScenarioFilters'
import { HazardScenario } from '@app/domains/hazards/types/hazard.types'
import { isLoadableData, isLoadableLoading } from '@app/state/jotai.utils'

type UseDataLayerScenarioParams = {
  layerId: string
  selectedAssetTag: string | null
  onSelectScenario: (assetTag: string | null) => void
  fallbackDisplayName?: string
  fallbackTilesetIds?: string[]
}

export const useDataLayerScenario = ({
  layerId,
  selectedAssetTag,
  onSelectScenario,
  fallbackDisplayName,
  fallbackTilesetIds = [],
}: UseDataLayerScenarioParams) => {
  const hazardLoadable = useAtomValue(hazardByIdLoadableAtom(layerId))
  const isHazardLoading = isLoadableLoading(hazardLoadable)
  const hazardData = isLoadableData(hazardLoadable) ? hazardLoadable.data : null
  const activeFilters = useAtomValue(scenarioFiltersAtomFamily(layerId))

  const allScenarios = (hazardData?.hazard.scenarios ?? []) as HazardScenario[]
  const hazardScopedScenarios = allScenarios.filter(
    (s) => s.hazard_name === hazardData?.scenario.hazard_name,
  )

  const gridParams = resolveScenarioGridParameters(hazardScopedScenarios)
  const filterableParams = resolveFilterableParameters(hazardScopedScenarios, gridParams)
  const visibleScenarios = filterHazardScenarios(hazardScopedScenarios, activeFilters)
  const grid = buildScenarioGrid(visibleScenarios, gridParams, hazardScopedScenarios)
  const selectedScenario = allScenarios.find((s) => s.assetTag === selectedAssetTag) ?? null

  const activeDisplayName = selectedScenario?.display_name ?? fallbackDisplayName

  const activeTilesetIds =
    selectedScenario?.tilesets?.map((t) => t.id).filter((tid): tid is string => !!tid) ??
    fallbackTilesetIds

  const { handleSelectScenario, handleResetFilters } = useHazardScenarioFilters({
    layerId,
    filteredScenarios: hazardScopedScenarios,
    filterableParams,
    selectedAssetTag,
    onSelectScenario,
  })

  return {
    selectedScenario,
    activeDisplayName,
    activeTilesetIds,
    filterableParams,
    grid,
    handleSelectScenario,
    handleResetFilters,
    hazardScopedScenarios,
    isHazardLoading,
  }
}
